import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  decreaseCart,
  getTotals,
  incrementCart,
  removeFromCart,
} from "../../store/reducers/cartSlice";
import { useSelector, useDispatch } from "react-redux";
import imgUrl from "../../imgUrl";
import { useSettings } from "../../context/SettingsContext";
import api from "../../api";

function OrderSummary({ onCouponChange, onShippingChange, shippingCost = 0 }) {
  const { currencySymbol } = useSettings();
  const cart = useSelector((state) => state.cart);
  const { cartTotalAmount } = useSelector((state) => state.cart);
  const dispatch = useDispatch();

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponName, setCouponName] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);

  useEffect(() => {
    dispatch(getTotals());
  }, [cart, dispatch]);

  // Notify parent of coupon changes
  useEffect(() => {
    if (onCouponChange) {
      onCouponChange({
        couponCode: couponApplied ? couponCode : "",
        couponDiscount,
      });
    }
  }, [couponDiscount, couponApplied, couponCode, onCouponChange]);

  const handleApplyCoupon = useCallback(async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    setCouponLoading(true);
    setCouponError("");
    setCouponSuccess("");

    try {
      const res = await api.post("/coupons/validate", {
        code: couponCode.trim().toUpperCase(),
        order_amount: cartTotalAmount,
      });

      const data = res?.data?.data;

      const discount = Number(data?.discount_amount ?? 0);
      const name = data?.coupon?.name || couponCode;

      setCouponDiscount(discount);
      setCouponName(name);
      setCouponApplied(true);

      setCouponSuccess(
        `✅ "${name}" applied! You save ${currencySymbol} ${discount.toFixed(2)}`,
      );
    } catch (err) {
      console.log("Coupon apply error:", err);

      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Invalid or expired coupon code.";

      setCouponError(msg);
      setCouponDiscount(0);
      setCouponApplied(false);
    } finally {
      setCouponLoading(false);
    }
  }, [couponCode, cartTotalAmount, currencySymbol]);

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setCouponDiscount(0);
    setCouponName("");
    setCouponApplied(false);
    setCouponError("");
    setCouponSuccess("");
  };

  const finalTotal = cartTotalAmount + shippingCost - couponDiscount;

  return (
    <div className="md:w-full lg:w-2/5 lg:ml-10 xl:ml-14 md:ml-6 flex flex-col h-full md:sticky lg:sticky top-28 md:order-2 lg:order-2">
      <div className="border p-2 lg:px-8 lg:py-8 rounded-lg bg-white order-1 sm:order-2">
        <h2 className="font-semibold text-black text-lg pb-4">Order Summary</h2>

        {/* Cart Items */}
        <div className="overflow-y-scroll flex-grow scrollbar w-full max-h-64 bg-gray-50 block">
          {cart.cartItems.length === 0 ? (
            <div className="text-center py-10">
              <span className="flex justify-center my-auto text-gray-500 font-semibold text-4xl">
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  height="1em"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M454.65 169.4A31.82 31.82 0 00432 160h-64v-16a112 112 0 00-224 0v16H80a32 32 0 00-32 32v216c0 39 33 72 72 72h272a72.22 72.22 0 0050.48-20.55 69.48 69.48 0 0021.52-50.2V192a31.75 31.75 0 00-9.35-22.6zM176 144a80 80 0 01160 0v16H176zm192 96a112 112 0 01-224 0v-16a16 16 0 0132 0v16a80 80 0 00160 0v-16a16 16 0 0132 0z" />
                </svg>
              </span>
              <h2 className="font-medium text-sm pt-2 text-gray-600">
                No Item Added Yet!
              </h2>
            </div>
          ) : (
            cart.cartItems.map((cartItem, index) => {
              const itemPrice = cartItem.selectedVariant
                ? Number(cartItem.selectedVariant.effective_price || 0)
                : Number(
                  cartItem?.offer_price
                    ? cartItem?.offer_price
                    : cartItem?.price,
                );
              const displayImg =
                cartItem.selectedVariant?.image || cartItem?.main_image;
              const stockQty =
                cartItem.selectedVariant?.stock_quantity ??
                cartItem?.stock_quantity ??
                Infinity;
              const atMax = cartItem.cartQuantity >= stockQty;

              return (
                <div
                  key={index}
                  className="group w-full h-auto flex justify-start items-center bg-white py-3 px-4 border-b hover:bg-gray-50 transition-all border-gray-100 relative last:border-b-0"
                >
                  <div className="relative flex rounded-full border border-gray-100 shadow-sm overflow-hidden flex-shrink-0 cursor-pointer mr-4">
                    <img
                      src={`${imgUrl}/${displayImg}`}
                      width="40"
                      height="40"
                      alt={cartItem?.name?.slice(0, 6)}
                    />
                  </div>
                  <div className="flex flex-col w-full overflow-hidden">
                    <Link
                      className="truncate text-sm font-medium !no-underline !text-gray-700 text-heading line-clamp-1"
                      to={`/product/${cartItem?.slug}`}
                    >
                      {cartItem.name}
                    </Link>
                    {cartItem.selectedVariant && (
                      <span className="text-[11px] text-emerald-600 font-semibold block leading-none mb-1">
                        Option: {cartItem.selectedVariant.label}
                      </span>
                    )}
                    <span className="text-xs text-gray-400 mb-1">
                      Item Price {currencySymbol} {itemPrice}
                    </span>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-sm md:text-base text-heading leading-5">
                        <span className="text-black">
                          {currencySymbol} {itemPrice * cartItem.cartQuantity}
                        </span>
                      </div>
                      <div className="h-8 w-22 md:w-24 lg:w-24 flex flex-wrap items-center justify-evenly p-1 border border-gray-100 bg-white text-gray-600 rounded-md">
                        <button
                          onClick={() => dispatch(decreaseCart(cartItem))}
                        >
                          <span className="text-dark text-base">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M18 12H6"
                              />
                            </svg>
                          </span>
                        </button>
                        <p className="text-sm font-semibold text-dark px-1">
                          {cartItem.cartQuantity}
                        </p>
                        <button
                          onClick={() => dispatch(incrementCart(cartItem))}
                          disabled={atMax}
                          title={atMax ? "Max stock reached" : ""}
                          className={
                            atMax ? "opacity-30 cursor-not-allowed" : ""
                          }
                        >
                          <span className="text-dark text-base">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                              />
                            </svg>
                          </span>
                        </button>
                      </div>
                      <button
                        onClick={() => dispatch(removeFromCart(cartItem))}
                        className="hover:text-red-600 text-red-400 text-lg cursor-pointer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Coupon Section */}
        <div className="mt-4 py-4 text-sm w-full font-semibold text-heading">
          {couponApplied ? (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
              <div>
                <span className="text-emerald-700 font-bold text-sm">
                  🎟️ {couponName}
                </span>
                <p className="text-emerald-600 text-xs mt-0.5">
                  Saved {currencySymbol} {couponDiscount.toFixed(2)}
                </p>
              </div>
              <button
                onClick={handleRemoveCoupon}
                className="text-xs text-red-400 hover:text-red-600 font-medium transition ml-3"
              >
                Remove
              </button>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleApplyCoupon();
              }}
              className="w-full"
            >
              <div className="flex flex-row items-center justify-between gap-2 w-full">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                    setCouponError("");
                  }}
                  placeholder="Enter coupon code"
                  className="form-input py-2 px-3 md:px-4 flex-1 appearance-none transition ease-in-out border text-input text-sm rounded-md h-12 duration-200 bg-white border-gray-200 focus:ring-0 focus:outline-none focus:border-emerald-500 placeholder-gray-500 placeholder-opacity-75 uppercase"
                />
                <button
                  type="submit"
                  disabled={couponLoading}
                  className="md:text-sm text-black leading-4 inline-flex items-center cursor-pointer transition ease-in-out duration-300 font-semibold text-center justify-center border border-gray-200 rounded-md placeholder-white focus-visible:outline-none focus:outline-none px-4 sm:px-6 py-3 hover:text-white hover:bg-emerald-500 h-12 text-sm whitespace-nowrap disabled:opacity-60 mr-2"
                >
                  {couponLoading ? "..." : "Apply"}
                </button>
              </div>
              {couponError && (
                <p className="text-red-500 text-xs mt-1.5">{couponError}</p>
              )}
            </form>
          )}
          {couponSuccess && !couponError && (
            <p className="text-emerald-600 text-xs mt-1.5">{couponSuccess}</p>
          )}
        </div>

        {/* Totals */}
        <div className="flex items-center py-2 text-sm w-full font-semibold text-gray-500">
          Subtotal
          <span className="ml-auto flex-shrink-0 text-gray-800 font-bold">
            {currencySymbol} {cartTotalAmount.toFixed(2)}
          </span>
        </div>
        <div className="flex items-center py-2 text-sm w-full font-semibold text-gray-500">
          Shipping Cost
          <span className="ml-auto flex-shrink-0 text-gray-800 font-bold">
            {shippingCost > 0
              ? `${currencySymbol} ${shippingCost.toFixed(2)}`
              : "—"}
          </span>
        </div>
        {couponDiscount > 0 && (
          <div className="flex items-center py-2 text-sm w-full font-semibold text-gray-500">
            Discount
            <span className="ml-auto flex-shrink-0 font-bold text-orange-500">
              − {currencySymbol} {couponDiscount.toFixed(2)}
            </span>
          </div>
        )}
        <div className="border-t mt-4 text-black">
          <div className="flex items-center font-bold justify-between pt-5 text-sm uppercase">
            Total Cost
            <span className="font-extrabold text-lg">
              {currencySymbol} {Math.max(0, finalTotal).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderSummary;
